import {
  type DropTargetDropEvent,
  type DropTargetOptions,
  Loading,
  // MenuItem,
  toast,
  Tooltip,
} from '@affine/component';
// import { Guard } from '@affine/core/components/guard';
import { useAppSettingHelper } from '@affine/core/components/hooks/affine/use-app-setting-helper';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { DocsService } from '@affine/core/modules/doc';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import { DocsSearchService } from '@affine/core/modules/docs-search';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { GlobalContextService } from '@affine/core/modules/global-context';
import { useExpandState } from '@affine/core/components/hooks/use-expand-state';
import {
  OrganizeService,
  DocTitleUniquenessService,
  type FolderNode,
  // OrganizeService,
} from '@affine/core/modules/organize';
import { GuardService } from '@affine/core/modules/permissions';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
// import {
// DeleteIcon,
// FolderIcon,
// PageIcon,
// PlusIcon,
// PlusThickIcon,
// RemoveFolderIcon,
// TagsIcon,
// } from '@blocksuite/icons/rc';
// import {
//   // DeleteIcon,
//   // FolderIcon,
//   // PageIcon,
//   // PlusIcon,
//   // PlusThickIcon,
//   RemoveFolderIcon,
//   // TagsIcon,
// } from '@blocksuite/icons/rc';
import {
  LiveData,
  MANUALLY_STOP,
  useLiveData,
  useService,
  useServices,
} from '@toeverything/infra';
import {
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { NEVER } from 'rxjs';

import {
  NavigationPanelTreeNode,
  type NavigationPanelTreeNodeDropEffect,
} from '../../tree';
// import type { NodeOperation } from '../../tree/types';
// import { NavigationPanelFolderNode } from '../folder';
import type { GenericNavigationPanelNode } from '../types';
import { Empty } from './empty';
import { useNavigationPanelDocNodeOperations } from './operations';
import * as styles from './styles.css';
import IconFont from '@ys/repo-ui/base/IconFont';
import { GlobalCache } from '@affine/core/modules/storage';
import { getEnv } from '@affine/core/modules/cloud/constant';

//  yuanshi-20250731: 解决循环依赖
const NavigationPanelFolderNode = lazy(() =>
  import('../folder').then(module => ({
    default: module.NavigationPanelFolderNode,
  }))
);

export const NavigationPanelDocNode = ({
  node,
  docId,
  onDrop,
  location,
  reorderable,
  // isLinked,
  canDrop,
  operations: additionalOperations,
  dropEffect,
}: {
  node: FolderNode;
  docId: string;
  isLinked?: boolean;
  forwardKey?: string;
} & GenericNavigationPanelNode) => {
  const t = useI18n();
  const {
    docsSearchService,
    docsService,
    globalContextService,
    docDisplayMetaService,
    featureFlagService,
    guardService,
    organizeService,
    docTitleUniquenessService,
  } = useServices({
    DocsSearchService,
    DocsService,
    GlobalContextService,
    DocDisplayMetaService,
    FeatureFlagService,
    GuardService,
    OrganizeService,
    DocTitleUniquenessService,
  });
  const globalCache = useService(GlobalCache);
  const { appSettings } = useAppSettingHelper();
  const { expanded: isExpanded, toggleExpanded } = useExpandState(
    node.id!,
    false
  );

  const active =
    useLiveData(globalContextService.globalContext.docId.$) === docId;
  const enableShimmer = useLiveData(organizeService.shimmer.isShimmerEnabled$);
  const shouldnotCollapseNodes = useLiveData(
    organizeService.shouldnotCollapseNodes$
  );

  const targetId = useLiveData(organizeService.shimmer.shimmerTargetId$);
  const shouldShimmer = useMemo(() => {
    return enableShimmer && targetId === docId;
  }, [enableShimmer, targetId, docId]);

  // const [collapsed, setCollapsed] = useState(true);
  // // 为每个文件夹节点创建唯一的存储键
  // const folderExpandKey = `folder.expand.${node.id}`;

  // 从缓存中读取初始展开状态，默认为折叠

  const [collapsed, setCollapsed] = useState<boolean>(!isExpanded);
  const env = useMemo(() => getEnv(), []);
  const isCollapsed = appSettings.showLinkedDocInSidebar ? !isExpanded : true;

  const docRecord = useLiveData(docsService.list.doc$(docId));
  const conflictDocs = useLiveData(organizeService.conflictDocs$);
  const filteredChildren = useLiveData(node?.filteredChildren$);
  const sortedChildren = useLiveData(node?.sortedChildren$);
  // const DocIcon = useLiveData(
  //   docDisplayMetaService.icon$(docId, {
  //     reference: isLinked,
  //   })
  // );
  const docTitle = useLiveData(docDisplayMetaService.title$(docId));
  const isInTrash = useLiveData(docRecord?.trash$);
  const enableEmojiIcon = useLiveData(
    featureFlagService.flags.enable_emoji_doc_icon.$
  );


  const linkChildren = useLiveData(
    useMemo(
      () =>
        LiveData.from(
          !isCollapsed ? docsSearchService.watchRefsFrom(docId) : NEVER,
          null
        ),
      [docsSearchService, docId, isCollapsed]
    )
  );
  const searching = linkChildren === null;

  const children = env === 'prod' ? filteredChildren : sortedChildren;

  
  const Icon = useCallback(({ className }: { className?: string }) => {
    const hasChildren = children.length > 0;
    const isBriefDoc = docTitle === '.Brief';
    
    let iconName = 'file';
    if (hasChildren) {
      iconName = 'folder';
    }
    if (isBriefDoc) {
      iconName = 'brief';
    }
    
    return (
      <IconFont
        name={iconName}
        size={24}
        className={className}
      />
    );
  }, [docTitle, children.length]);

  const [referencesLoading, setReferencesLoading] = useState(true);
  // 在点击展开/折叠时调用
  const handleToggleExpand = useCallback(() => {
    toggleExpanded();
  }, [toggleExpanded]);
  useLayoutEffect(() => {
    const abortController = new AbortController();
    docsSearchService.indexer
      .waitForDocCompletedWithPriority(docId, 100, abortController.signal)
      .then(() => {
        setReferencesLoading(false);
      })
      .catch(err => {
        if (err !== MANUALLY_STOP) {
          console.error(err);
        }
      });
    return () => {
      abortController.abort(MANUALLY_STOP);
    };
  }, [docId, docsSearchService]);

  const dndData = useMemo(() => {
    return {
      draggable: {
        entity: {
          type: 'doc',
          id: docId,
        },
        from: location,
      },
      dropTarget: {
        at: 'navigation-panel:doc',
      },
    } satisfies AffineDNDData;
  }, [docId, location]);

  const handleRename = useAsyncCallback(
    async (newName: string) => {
      // 获取当前文档所在的文件夹节点
      const currentNode =
        organizeService.folderTree.folderNodeByDocId$(docId).value;
      if (!currentNode) {
        console.warn('Cannot find folder node for document:', docId);
        return;
      }

      // 获取父节点
      const nodeInfo = currentNode.info$.value;
      const parentId = nodeInfo?.parentId;
      let parentNode = organizeService.folderTree.rootFolder;

      if (parentId) {
        const parentNodeValue =
          organizeService.folderTree.folderNode$(parentId)?.value;
        if (parentNodeValue) {
          parentNode = parentNodeValue;
        }
      }

      // 确保标题唯一性并获取最终标题
      const finalTitle =
        await docTitleUniquenessService.ensureUniqueTitleInFolder(
          docId,
          parentNode,
          newName
        );

      // 如果最终标题与输入的标题不同，说明标题被修正了
      if (finalTitle !== newName) {
        console.log(`Title corrected from "${newName}" to "${finalTitle}"`);
      }
      track.$.navigationPanel.docs.asset_item_click({
        click_event: 'rename',
      });
    },
    [docId, docTitleUniquenessService, organizeService]
  );

  const handleDropOnFolder = useCallback(
    async (data: DropTargetDropEvent<AffineDNDData>) => {
      if (data.source.data.entity?.type) {
        track.$.navigationPanel.folders.drop({
          type: data.source.data.entity.type,
        });
      }
      if (data.treeInstruction?.type === 'make-child') {
        if (data.source.data.entity?.type === 'folder') {
          if (
            node.id === data.source.data.entity.id ||
            node.beChildOf(data.source.data.entity.id)
          ) {
            return;
          }
          node.moveHere(data.source.data.entity.id, node.indexAt('before'));
          track.$.navigationPanel.organize.moveOrganizeItem({ type: 'folder' });
        } else if (
          data.source.data.entity?.type === 'collection' ||
          data.source.data.entity?.type === 'doc' ||
          data.source.data.entity?.type === 'tag'
        ) {
          if (
            data.source.data.from?.at ===
            'navigation-panel:organize:folder-node'
          ) {
            node.moveHere(data.source.data.from.nodeId, node.indexAt('before'));
            track.$.navigationPanel.organize.moveOrganizeItem({
              type: 'link',
              target: data.source.data.entity?.type,
            });
          } else {
            // 如果是文档类型，确保标题唯一性
            if (data.source.data.entity?.type === 'doc') {
              try {
                await docTitleUniquenessService.ensureUniqueTitleInFolder(
                  data.source.data.entity.id,
                  node
                );
              } catch (error) {
                console.warn('Failed to ensure title uniqueness:', error);
              }
            }

            node.createLink(
              data.source.data.entity?.type,
              data.source.data.entity.id,
              node.indexAt('before')
            );
            track.$.navigationPanel.organize.createOrganizeItem({
              type: 'link',
              target: data.source.data.entity?.type,
            });
          }
        }
      } else {
        onDrop?.(data);
      }
    },
    [node, onDrop, docTitleUniquenessService]
  );

  const handleDropOnDoc = useAsyncCallback(
    async (data: DropTargetDropEvent<AffineDNDData>) => {
      if (data.treeInstruction?.type === 'make-child') {
        if (data.source.data.entity?.type === 'doc') {
          const canEdit = await guardService.can('Doc_Update', docId);
          if (!canEdit) {
            toast(t['com.affine.no-permission']());
            return;
          }
          // await docsService.addLinkedDoc(docId, data.source.data.entity.id);
          track.$.navigationPanel.docs.linkDoc({
            control: 'drag',
          });
          track.$.navigationPanel.docs.drop({
            type: data.source.data.entity.type,
          });
        } else {
          toast(t['com.affine.rootAppSidebar.doc.link-doc-only']());
        }
      } else {
        onDrop?.(data);
      }

      await handleDropOnFolder(data);
    },
    [docId, docsService, guardService, onDrop, t, handleDropOnFolder]
  );

  const handleDropEffectOnDoc = useCallback<NavigationPanelTreeNodeDropEffect>(
    data => {
      if (data.treeInstruction?.type === 'make-child') {
        if (data.source.data.entity?.type === 'doc') {
          return 'link';
        }
      } else {
        return dropEffect?.(data);
      }
      return;
    },
    [dropEffect]
  );

  const handleDropOnPlaceholder = useAsyncCallback(
    async (data: DropTargetDropEvent<AffineDNDData>) => {
      if (data.source.data.entity?.type === 'doc') {
        const canEdit = await guardService.can('Doc_Update', docId);
        if (!canEdit) {
          toast(t['com.affine.no-permission']());
          return;
        }
        // TODO(eyhn): timeout&error handling
        // await docsService.addLinkedDoc(docId, data.source.data.entity.id);
        track.$.navigationPanel.docs.linkDoc({
          control: 'drag',
        });
        track.$.navigationPanel.docs.drop({
          type: data.source.data.entity.type,
        });
      } else {
        toast(t['com.affine.rootAppSidebar.doc.link-doc-only']());
      }
    },
    [docId, docsService, guardService, t]
  );

  const handleDropOnChildren = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, dropAtNode?: FolderNode) => {
      if (!dropAtNode || !dropAtNode.id) {
        return;
      }
      if (data.source.data.entity?.type) {
        track.$.navigationPanel.folders.drop({
          type: data.source.data.entity.type,
        });
      }
      if (
        data.treeInstruction?.type === 'reorder-above' ||
        data.treeInstruction?.type === 'reorder-below'
      ) {
        const at =
          data.treeInstruction?.type === 'reorder-below' ? 'after' : 'before';
        if (data.source.data.entity?.type === 'folder') {
          if (
            node.id === data.source.data.entity.id ||
            node.beChildOf(data.source.data.entity.id)
          ) {
            return;
          }
          node.moveHere(
            data.source.data.entity.id,
            node.indexAt(at, dropAtNode.id)
          );
          track.$.navigationPanel.organize.moveOrganizeItem({ type: 'folder' });
        } else if (
          data.source.data.entity?.type === 'collection' ||
          data.source.data.entity?.type === 'doc' ||
          data.source.data.entity?.type === 'tag'
        ) {
          if (
            data.source.data.from?.at ===
            'navigation-panel:organize:folder-node'
          ) {
            node.moveHere(
              data.source.data.from.nodeId,
              node.indexAt(at, dropAtNode.id)
            );
            track.$.navigationPanel.organize.moveOrganizeItem({
              type: 'link',
              target: data.source.data.entity?.type,
            });
          } else {
            node.createLink(
              data.source.data.entity?.type,
              data.source.data.entity.id,
              node.indexAt(at, dropAtNode.id)
            );

            track.$.navigationPanel.organize.createOrganizeItem({
              type: 'link',
              target: data.source.data.entity?.type,
            });
          }
        }
      } else if (data.treeInstruction?.type === 'reparent') {
        const currentLevel = data.treeInstruction.currentLevel;
        const desiredLevel = data.treeInstruction.desiredLevel;
        if (currentLevel === desiredLevel + 1) {
          onDrop?.({
            ...data,
            treeInstruction: {
              type: 'reorder-below',
              currentLevel,
              indentPerLevel: data.treeInstruction.indentPerLevel,
            },
          });
          return;
        } else {
          onDrop?.({
            ...data,
            treeInstruction: {
              ...data.treeInstruction,
              currentLevel: currentLevel - 1,
            },
          });
        }
      }
    },
    [node, onDrop]
  );

  const childrenOperations = useCallback(
    (type: string, node: FolderNode) => {
      //  yuanshi-20250807-删除从文件夹删除内容逻辑
      // if (type === 'doc' || type === 'collection' || type === 'tag') {
      //   return [
      //     {
      //       index: 999,
      //       view: (
      //         <MenuItem
      //           type={'danger'}
      //           prefixIcon={<RemoveFolderIcon />}
      //           data-event-props="$.navigationPanel.organize.deleteOrganizeItem"
      //           data-event-args-type={node.type$.value}
      //           onClick={() => node.delete()}
      //         >
      //           {t['com.affine.rootAppSidebar.organize.delete-from-folder']()}
      //         </MenuItem>
      //       ),
      //     },
      //   ] satisfies NodeOperation[];
      // }
      return [];
    },
    [
      // t
    ]
  );

  const handleDropEffectOnChildren =
    useCallback<NavigationPanelTreeNodeDropEffect>(
      data => {
        if (
          data.treeInstruction?.type === 'reorder-above' ||
          data.treeInstruction?.type === 'reorder-below'
        ) {
          if (data.source.data.entity?.type === 'folder') {
            if (
              node.id === data.source.data.entity.id ||
              node.beChildOf(data.source.data.entity.id)
            ) {
              return;
            }
            return 'move';
          } else if (
            data.source.data.from?.at ===
            'navigation-panel:organize:folder-node'
          ) {
            return 'move';
          } else if (
            data.source.data.entity?.type === 'collection' ||
            data.source.data.entity?.type === 'doc' ||
            data.source.data.entity?.type === 'tag'
          ) {
            return 'link';
          }
        } else if (data.treeInstruction?.type === 'reparent') {
          const currentLevel = data.treeInstruction.currentLevel;
          const desiredLevel = data.treeInstruction.desiredLevel;
          if (currentLevel === desiredLevel + 1) {
            dropEffect?.({
              ...data,
              treeInstruction: {
                type: 'reorder-below',
                currentLevel,
                indentPerLevel: data.treeInstruction.indentPerLevel,
              },
            });
            return;
          } else {
            dropEffect?.({
              ...data,
              treeInstruction: {
                ...data.treeInstruction,
                currentLevel: currentLevel - 1,
              },
            });
          }
        }
        return;
      },
      [dropEffect, node]
    );

  const handleChildrenCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      const entityType = args.source.data.entity?.type;

      if (args.source.data.entity?.type === 'folder') {
        if (
          node.id === args.source.data.entity.id ||
          node.beChildOf(args.source.data.entity.id)
        ) {
          return false;
        }
        return true;
      } else if (
        args.source.data.from?.at === 'navigation-panel:organize:folder-node'
      ) {
        return true;
      } else if (
        entityType === 'collection' ||
        entityType === 'doc' ||
        entityType === 'tag'
      ) {
        return true;
      }
      return false;
    },
    [node]
  );

  const handleCollapsedChange = useCallback(
    (collapsed: boolean) => {
      handleToggleExpand();
      if (collapsed) {
        setCollapsed(true);
        // 保存折叠状态到缓存
      } else {
        setCollapsed(false);
        // 保存展开状态到缓存
      }
    },
    [globalCache, handleToggleExpand]
  );

  const handleCanDrop = useMemo<DropTargetOptions<AffineDNDData>['canDrop']>(
    () => args => {
      const entityType = args.source.data.entity?.type;
      return args.treeInstruction?.type !== 'make-child'
        ? ((typeof canDrop === 'function' ? canDrop(args) : canDrop) ?? true)
        : entityType === 'doc';
    },
    [canDrop]
  );

  const workspaceDialogService = useService(WorkspaceDialogService);
  const operations = useNavigationPanelDocNodeOperations(
    docId,
    useMemo(
      () => ({
        openInfoModal: () => workspaceDialogService.open('doc-info', { docId }),
        openNodeCollapsed: () => setCollapsed(false),
      }),
      [docId, workspaceDialogService]
    ),
    node
  );

  const finalOperations = useMemo(() => {
    if (additionalOperations) {
      return [...operations, ...additionalOperations];
    }
    return operations;
  }, [additionalOperations, operations]);

  useEffect(() => {
    if (!shouldnotCollapseNodes.includes(docId)) return;
    const id = requestAnimationFrame(() => setCollapsed(false));
    return () => cancelAnimationFrame(id);
  }, [shouldnotCollapseNodes, docId]);

  if (isInTrash || !docRecord) {
    return null;
  }
  return (
    <NavigationPanelTreeNode
      icon={Icon}
      name={t.t(docTitle)}
      dndData={dndData}
      onDrop={handleDropOnDoc}
      renameable
      extractEmojiAsIcon={enableEmojiIcon}
      collapsed={collapsed}
      setCollapsed={handleCollapsedChange}
      collapsible={children.length > 0}
      canDrop={handleCanDrop}
      to={`/${docId}?refer=asset_click`}
      onClick={() => {
        track.$.navigationPanel.docs.asset_item_click({
          click_event: 'open_doc',
        });
      }}
      shouldShowBlackPoint={conflictDocs.includes(docId)}
      shouldShimmer={shouldShimmer}
      active={active}
      postfix={
        referencesLoading &&
        !collapsed && (
          <Tooltip
            content={t['com.affine.rootAppSidebar.docs.references-loading']()}
          >
            <div className={styles.loadingIcon}>
              <Loading />
            </div>
          </Tooltip>
        )
      }
      reorderable={reorderable}
      renameableGuard={{
        docId,
        action: 'Doc_Update',
      }}
      onRename={handleRename}
      childrenPlaceholder={
        searching ? null : (
          <Empty
            onDrop={handleDropOnPlaceholder}
            noAccessible={!!children && children.length > 0}
          />
        )
      }
      operations={finalOperations}
      dropEffect={handleDropEffectOnDoc}
      data-testid={`navigation-panel-doc-${docId}`}
    >
      {/* {
        // yuanshi-20250730: 文档间的link关系暂时不展示了，通过showLinkedDocInSidebar控制的
        appSettings.showLinkedDocInSidebar && children?.length > 0 ? (
          <Guard docId={docId} permission="Doc_Read">
            {canRead =>
              canRead
                ? linkChildren?.map((child, index) => (
                    <NavigationPanelDocNode
                      key={`${child.docId}-${index}`}
                      docId={child.docId}
                      reorderable={false}
                      location={{
                        at: 'navigation-panel:doc:linked-docs',
                        docId,
                      }}
                      isLinked
                    />
                  ))
                : null
            }
          </Guard>
        ) : null
      } */}
      {children?.map(child => (
        <NavigationPanelFolderNode
          key={child.id}
          nodeId={child.id as string}
          defaultRenaming={true}
          onDrop={handleDropOnChildren}
          operations={childrenOperations}
          dropEffect={handleDropEffectOnChildren}
          canDrop={handleChildrenCanDrop}
          location={{
            at: 'navigation-panel:organize:folder-node',
            nodeId: child.id as string,
          }}
        />
      ))}
    </NavigationPanelTreeNode>
  );
};
